import { useState, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { CardType } from './Card';
import Card from './Card';

export interface CardData {
  id: string;
  type: CardType;
  content: {
    text: string;
    author?: string;
    imageUrl?: string;
    options?: string[];
  };
}

interface CardStackProps {
  cards: CardData[];
  onCardSwiped?: (cardId: string, direction: 'left' | 'right') => void;
  onAllCardsSwiped?: () => void;
}

export function CardStack({ cards, onCardSwiped, onAllCardsSwiped }: CardStackProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<number>(1);

  const handleSwipe = useCallback((direction: 'left' | 'right') => {
    const currentCardId = cards[currentIndex]?.id;
    
    // Notify parent about the swipe
    onCardSwiped?.(currentCardId, direction);
    
    // Always move to next index to allow last card to animate out
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    setSwipeDirection(direction === 'right' ? 1 : -1);
    
    // Check if all cards have been swiped after a brief delay
    if (newIndex >= cards.length) {
      setTimeout(() => {
        onAllCardsSwiped?.();
      }, 300); // Match the exit animation duration
    }
  }, [currentIndex, cards, onCardSwiped, onAllCardsSwiped]);

  const resetCards = useCallback(() => {
    setCurrentIndex(0);
  }, []);

  // If no cards or all cards swiped
  if (cards.length === 0 || currentIndex >= cards.length) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] max-h-[600px] w-full">
        <div className="text-center p-8 max-w-md">
          <div className="text-5xl mb-4">✨</div>
          <h2 className="text-2xl font-bold mb-2 text-muted-foreground">All done for now!</h2>
          <p className="text-muted-foreground mb-6">
            Come back tomorrow for more lovely ideas from your Wingman
          </p>
          <button
            onClick={resetCards}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-colors"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  // Only render the top 5 cards for performance
  const visibleCards = cards.slice(currentIndex, currentIndex + 5);
  
  if (cards.length === 0 || currentIndex >= cards.length) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] max-h-[600px] w-full">
        <div className="text-center p-8 max-w-md">
          <div className="text-5xl mb-4">✨</div>
          <h2 className="text-2xl font-bold mb-2">All done for now!</h2>
          <p className="text-muted-foreground mb-6">
            Come back tomorrow for more lovely ideas from your Wingman
          </p>
          <button
            onClick={resetCards}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-colors"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[70vh] max-h-[600px] flex items-center justify-center mx-auto overflow-hidden">
      <div className="relative w-full h-full" style={{ perspective: '1000px' }}>
        <AnimatePresence custom={swipeDirection}>
          {visibleCards.map((card, index) => (
            <div 
              key={card.id}
              className="absolute inset-0 flex items-center justify-center"
              style={{
                zIndex: visibleCards.length - index,
                transformStyle: 'preserve-3d'
              }}
            >
              <Card
                id={card.id}
                type={card.type}
                content={card.content}
                isActive={index === 0}
                onSwipe={handleSwipe}
              />
            </div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
